import React, {useState, useEffect, useContext} from "react"
import PropTypes from 'prop-types'
import { transparentize } from 'polished'
import { theme } from "../../theme"
import EPCircularTrack from "../EPCircularTrack"
import { ThemeContext } from "styled-components"

const mapRounds = (value) => value ? Math.floor(value) : null
const mapValue = (value) => value ? value - mapRounds(value) : null
const getValue = ({value, rounds}) => value + rounds

const CircularTrackInput = ({value: v, pointerColor, progressColor, circleBackgroundColor, strokeWidth, radius, thumbRadius, minValue, onChange, maxValue, accuracy}) => {
    const validValue = Math.max(minValue, maxValue? Math.min(v, maxValue) : v)
    const maxRounds = maxValue && Math.ceil(maxValue)
    const calculateMaxValue = mapValue(maxValue)
    const [val, setVal] = useState({
      rounds: mapRounds(validValue),
      value: mapValue(validValue),
    })
    const themeContext = useContext(ThemeContext)
    const [tempValue, setTempValue] = useState(val.value)
    const [preValue, setPreValue] = useState(val.value)
    // value: [1 ... 0.7]
    const backgroundColorTransparency = 0.9 - (0.1 * (val.rounds/maxRounds))
    const backgroundColor = transparentize(
      backgroundColorTransparency,
      themeContext.colors[circleBackgroundColor]
    )
    // value: [(backgroundColorTransparency - 0.1) ... 0]
    const transparencyConstant = (val.value === 0 && val.rounds !== 0) ? 1 : val.value
    const strokeColorTransparency = 0.7 * (1 - (transparencyConstant * val.rounds / maxRounds))
    const strokeColor = transparentize(strokeColorTransparency, themeContext.colors[progressColor])

    const calculateRounds = (value) => {
        if (value < 0.1 && preValue > 0.9) {
            return val.rounds + 1
        } else if (preValue < 0.1 && value > 0.9) {
            return val.rounds - 1
        }
        return val.rounds
    }

    useEffect(() => {
      setVal({
        rounds: mapRounds(validValue),
        value: mapValue(validValue),
      })
      setPreValue(mapValue(validValue))
    }, [v])

    useEffect(() => {
      const tempRounds = calculateRounds(tempValue)
      if (maxRounds && calculateMaxValue && ((maxRounds - 1) === tempRounds) && (tempValue > calculateMaxValue)) {
        setVal({
          value: calculateMaxValue,
          rounds: maxRounds - 1,
        })
        onChange(maxValue)
      } else if (maxRounds && (tempRounds >= maxRounds)) {
        if (tempValue > 0.75) {
          setVal({
            value: tempValue,
            rounds: maxRounds - 1,
          })
        }
      } else if(tempRounds < 0) {
        if (tempValue < Math.min(minValue + 0.25, 1)) {
          setVal({
            value: tempValue,
            rounds: 0,
          })
        }
      } else if(minValue && ((tempRounds === 0) && (minValue > tempValue))) {
        setVal({
          value: minValue,
          rounds: 0,
        })
        onChange(minValue)
      } else {
        const val = {
          value: tempValue,
          rounds: tempRounds,
        }
        setVal(val)
        setPreValue(tempValue)
        onChange(getValue(val))
      }
    }, [tempValue, setVal])

    return <EPCircularTrack
        size={radius * 2}
        accuracy={accuracy}
        strokeWidth={strokeWidth}
        progressRadius={thumbRadius}
        onChange={setTempValue}
        value={val.value}
        backgroundColor={backgroundColor}
        progressColor={pointerColor}
        progressBarColor={strokeColor} />
}

CircularTrackInput.propTypes = {
    value: PropTypes.number,
    accuracy: PropTypes.number,
    rounds: PropTypes.number,
    maxValue: PropTypes.number,
    minValue: PropTypes.number.isRequired,
    pointerColor: PropTypes.oneOf(Object.keys(theme.colors)),
    circleBackgroundColor: PropTypes.string,
    progressColor: PropTypes.oneOf(Object.keys(theme.colors)),
    onChange: PropTypes.func,
    strokeWidth: PropTypes.number,
    radius: PropTypes.number,
    thumbRadius: PropTypes.number
}

CircularTrackInput.defaultProps = {
    minValue: 0,
    maxValue: null,
    strokeWidth: 80,
    accuracy: 0,
    radius: 150,
    thumbRadius: 30,
    circleBackgroundColor: 'black',
    onChange: () => {},
  }

export default CircularTrackInput